/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Search,
  ShoppingCart,
  Visibility,
  InfoOutlined,
  Clear,
  CheckCircle,
  CheckCircleOutline,
  HourglassTop,
  Cancel,
  RemoveCircleOutline,
  ContentPasteSearch,
  FilterList,
  List,
  ReceiptLong,
  RequestQuote,
  AddShoppingCart,
  Rule,
} from '@mui/icons-material';

import api from '../../../../../services/api';
import GenericSearchInput from '../../../../../components/common/GenericSearchInput';
import PurchaseRequestDetailsModal, {
  PurchaseRequestDetailData,
} from '../../../../../components/PurchaseRequestDetailsModal';

export interface PurchaseRequestRowData extends PurchaseRequestDetailData {
  overallStatus?: 'FINALIZADA' | 'GEROU_OC' | 'EM_COTACAO' | 'APROVADA' | 'EM_APROVACAO' | 'REPROVADA' | 'CANCELADA';
  numCotacao?: number | null;
  numOrdemCompra?: number | null;
  notaFiscalEntrada?: number | null;
  dataRecebimento?: string | null;
}

interface Branch {
  id: number;
  nome: string;
}

interface Company {
  id: number;
  nome: string;
  subtitulo: string;
  filiais: Branch[];
}

type SortableKeys =
  | 'solicitacao'
  | 'dataSolicitacao'
  | 'codUsuarioSolicitante'
  | 'descricaoProduto'
  | 'quantidadeSolicitada'
  | 'overallStatus';

const STORAGE_KEY = 'purchase_consultation_filters';

const formatNumberPtBR = (num?: number) => {
  if (num === undefined || num === null || Number.isNaN(Number(num))) return '0';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(Number(num));
};

const getSortableDateTime = (dateStr?: string, timeNum?: number) => {
  if (!dateStr) return 0;
  let yyyymmdd = '';
  const trimmed = dateStr.trim();

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [d, m, y] = trimmed.split('/');
    yyyymmdd = `${y}${m}${d}`;
  } else if (trimmed.includes('-')) {
    const clean = trimmed.split('T')[0];
    const [y, m, d] = clean.split('-');
    yyyymmdd = `${y}${m.padStart(2, '0')}${d.padStart(2, '0')}`;
  } else {
    yyyymmdd = trimmed;
  }

  const timeStr = String(timeNum ?? 0).padStart(4, '0');
  return Number(`${yyyymmdd}${timeStr}`);
};

function HighlightText({ text, highlight }: { text: string; highlight: string }) {
  const theme = useTheme();

  if (!highlight || !highlight.trim() || !text) {
    return <>{text}</>;
  }

  const tokens = highlight.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return <>{text}</>;

  const escapedTokens = tokens.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escapedTokens.join('|')})`, 'gi');
  const parts = String(text).split(regex);

  return (
    <>
      {parts.map((part, index) => {
        const isMatch = tokens.some(t => t.toLowerCase() === part.toLowerCase());
        return isMatch ? (
          <Box
            key={index}
            component="span"
            sx={{
              bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35),
              color: 'text.primary',
              fontWeight: 'bold',
              px: 0.3,
              borderRadius: '2px',
            }}
          >
            {part}
          </Box>
        ) : (
          part
        );
      })}
    </>
  );
}

export default function PurchaseRequestConsultation() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    const target = path.startsWith('/') ? path : `/${path}`;
    if (typeof navigate === 'function') {
      navigate(target);
    } else {
      window.location.href = target;
    }
  };

  // Leitura de parâmetros de busca da URL (Link direto de e-mail)
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const urlCompanyId = urlParams.get('companyId') ? Number(urlParams.get('companyId')) : null;
  const urlBranchId = urlParams.get('branchId') ? Number(urlParams.get('branchId')) : null;
  const urlRequestNum = urlParams.get('purchaseRequestNum') || urlParams.get('requestNum') || '';
  const urlStatus = urlParams.get('status') || '';

  const savedFilters = useMemo(() => {
    try {
      const item = localStorage.getItem(STORAGE_KEY);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  }, []);

  const [companyId, setCompanyId] = useState<number>(urlCompanyId ?? savedFilters?.companyId ?? 1);
  const [branchId, setBranchId] = useState<number>(urlBranchId ?? savedFilters?.branchId ?? 1);
  const [statusFilter, setStatusFilter] = useState<string>(urlStatus || savedFilters?.statusFilter || 'ALL');
  const [purchaseRequestNum, setPurchaseRequestNum] = useState<string>(
    urlRequestNum || savedFilters?.purchaseRequestNum || '',
  );
  const [startDate, setStartDate] = useState<string>(savedFilters?.startDate ?? '');
  const [endDate, setEndDate] = useState<string>(savedFilters?.endDate ?? '');
  const [requesterInput, setRequesterInput] = useState<string>(savedFilters?.requesterInput ?? '');
  const [itemCodeInput, setItemCodeInput] = useState<string>(savedFilters?.itemCodeInput ?? '');
  const [quickSearch, setQuickSearch] = useState<string>('');

  const [requests, setRequests] = useState<PurchaseRequestRowData[]>([]);
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const [sortField, setSortField] = useState<SortableKeys>('solicitacao');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState<boolean>(true);

  const [detailItem, setDetailItem] = useState<PurchaseRequestDetailData | null>(null);

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ open: true, message, type });
  };

  const handleResetSearchData = () => {
    setRequests([]);
    setPage(0);
    setHasSearched(false);
  };

  const executeFilterSearch = async (
    cId = companyId,
    bId = branchId,
    status = statusFilter,
    reqNum = purchaseRequestNum,
  ) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          companyId: cId,
          branchId: bId,
          statusFilter: status,
          purchaseRequestNum: reqNum,
          startDate,
          endDate,
          requesterInput,
          itemCodeInput,
        }),
      );
    } catch (e) {
      console.error('Erro ao salvar filtros no localStorage:', e);
    }

    try {
      setIsLoading(true);
      const { data } = await api.get<PurchaseRequestRowData[]>('erp/purchases/requests/consultation', {
        params: {
          companyId: cId,
          branchId: bId,
          status,
          purchaseRequestNum: reqNum.trim() || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          requesters: requesterInput.trim() || undefined,
          itemCode: itemCodeInput.trim() || undefined,
        },
      });
      setRequests(data || []);
      setPage(0);
      setHasSearched(true);
    } catch (error: any) {
      console.error('Erro ao buscar solicitações:', error);
      const msg = error?.response?.data?.message || 'Erro ao carregar solicitações de compra.';
      showNotification(msg, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchCompaniesAndInitialData = async () => {
      try {
        setIsLoadingCompanies(true);
        const { data } = await api.get<Company[]>('app/data-collection/get-company');
        if (data && Array.isArray(data) && data.length > 0) {
          setCompanyList(data);

          const targetCompanyId =
            urlCompanyId && data.some(c => c.id === urlCompanyId)
              ? urlCompanyId
              : savedFilters?.companyId && data.some(c => c.id === savedFilters.companyId)
                ? savedFilters.companyId
                : data[0].id;

          const foundCompany = data.find(c => c.id === targetCompanyId) || data[0];

          const targetBranchId =
            urlBranchId && foundCompany.filiais?.some(f => f.id === urlBranchId)
              ? urlBranchId
              : savedFilters?.branchId && foundCompany.filiais?.some(f => f.id === savedFilters.branchId)
                ? savedFilters.branchId
                : (foundCompany.filiais?.[0]?.id ?? 1);

          const targetStatus = urlStatus || statusFilter;
          const targetRequestNum = urlRequestNum || purchaseRequestNum;

          setCompanyId(targetCompanyId);
          setBranchId(targetBranchId);

          if (urlStatus) setStatusFilter(urlStatus);
          if (urlRequestNum) setPurchaseRequestNum(urlRequestNum);
          if (urlStatus || urlRequestNum)
            executeFilterSearch(targetCompanyId, targetBranchId, targetStatus, targetRequestNum);
        }
      } catch (error) {
        console.error('Erro ao carregar empresas:', error);
        showNotification('Erro ao carregar lista de empresas.', 'error');
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompaniesAndInitialData();
  }, []);

  const handleCompanyChange = (e: SelectChangeEvent<number>) => {
    const selectedCompanyId = Number(e.target.value);
    setCompanyId(selectedCompanyId);

    const foundCompany = companyList.find(emp => emp.id === selectedCompanyId);
    if (foundCompany && foundCompany.filiais && foundCompany.filiais.length > 0) {
      setBranchId(foundCompany.filiais[0].id);
    }
    handleResetSearchData();
  };

  const currentCompany = useMemo(() => {
    return companyList.find(e => e.id === companyId) || companyList[0] || { filiais: [] };
  }, [companyList, companyId]);

  const handleSortClick = (field: SortableKeys) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const filteredRequests = useMemo(() => {
    let result = requests;

    if (quickSearch.trim()) {
      const term = quickSearch.toLowerCase();
      result = result.filter(r => {
        const sol = String(r.solicitacao);
        const prod = (r.descricaoProduto || r.descricaoServico || '').toLowerCase();
        const codProd = (r.produto || r.servico || '').toLowerCase();
        const cc = (r.descricaoCentroCusto || '').toLowerCase();
        const codCc = (r.centroCusto || '').toLowerCase();
        const solicitante = `${r.codUsuarioSolicitante} ${r.nomeUsuarioSolicitante || ''}`.toLowerCase();
        const oc = String(r.numOrdemCompra || '');
        const cot = String(r.numCotacao || '');
        const nf = String(r.notaFiscalEntrada || '');
        const st = (r.overallStatus || '').toLowerCase();

        return (
          sol.includes(term) ||
          prod.includes(term) ||
          codProd.includes(term) ||
          cc.includes(term) ||
          codCc.includes(term) ||
          solicitante.includes(term) ||
          oc.includes(term) ||
          cot.includes(term) ||
          nf.includes(term) ||
          st.includes(term)
        );
      });
    }

    return [...result].sort((a, b) => {
      let aVal: any = a[sortField] ?? '';
      let bVal: any = b[sortField] ?? '';

      if (sortField === 'dataSolicitacao') {
        aVal = getSortableDateTime(a.dataSolicitacao, a.horaSolicitacao);
        bVal = getSortableDateTime(b.dataSolicitacao, b.horaSolicitacao);
      } else if (sortField === 'descricaoProduto') {
        aVal = a.descricaoProduto || a.descricaoServico || '';
        bVal = b.descricaoProduto || b.descricaoServico || '';
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [requests, quickSearch, sortField, sortOrder]);

  const paginatedRequests = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRequests.slice(start, start + rowsPerPage);
  }, [filteredRequests, page, rowsPerPage]);

  const formatMinutesToTime = (minutes?: number) => {
    if (minutes === undefined || minutes === null) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const getStatusChip = (row: PurchaseRequestRowData) => {
    switch (row.overallStatus) {
      case 'FINALIZADA':
        return (
          <Tooltip
            title={
              row.notaFiscalEntrada ? `Nota Fiscal Nº ${row.notaFiscalEntrada} recebida` : 'Atendida no ERP Sapiens'
            }
          >
            <Chip
              icon={<CheckCircleOutline fontSize="small" />}
              label={row.notaFiscalEntrada ? `Finalizada (NF #${row.notaFiscalEntrada})` : 'Finalizada'}
              color="success"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Tooltip>
        );
      case 'GEROU_OC':
        return (
          <Tooltip title={row.numOrdemCompra ? `Ordem de Compra Nº ${row.numOrdemCompra}` : 'Ordem de Compra Gerada'}>
            <Chip
              icon={<ReceiptLong fontSize="small" />}
              label={row.numOrdemCompra ? `OC #${row.numOrdemCompra}` : 'Gerou OC'}
              color="info"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Tooltip>
        );
      case 'EM_COTACAO':
        return (
          <Tooltip title={row.numCotacao ? `Cotação de Preços Nº ${row.numCotacao}` : 'Em Processo de Cotação'}>
            <Chip
              icon={<RequestQuote fontSize="small" />}
              label={row.numCotacao ? `Cotação #${row.numCotacao}` : 'Em Cotação'}
              color="primary"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Tooltip>
        );
      case 'APROVADA':
        return (
          <Tooltip title="Aprovada pela alçada - Na fila de compras">
            <Chip
              icon={<CheckCircle fontSize="small" />}
              label="Aprovada"
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Tooltip>
        );
      case 'REPROVADA':
        return (
          <Tooltip title="Reprovada pela alçada">
            <Chip
              icon={<Cancel fontSize="small" />}
              label="Reprovada"
              color="error"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Tooltip>
        );
      case 'CANCELADA':
        return (
          <Tooltip title="Solicitação Cancelada">
            <Chip
              icon={<RemoveCircleOutline />}
              label="Cancelada"
              color="error"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Tooltip>
        );
      default:
        return (
          <Tooltip title="Aguardando aprovação da alçada">
            <Chip
              icon={<HourglassTop fontSize="small" />}
              label="Pendente"
              color="warning"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Tooltip>
        );
    }
  };

  return (
    <Box pb={3}>
      {/* CABEÇALHO COM TÍTULO E BOTÕES DE NAVEGAÇÃO ALINHADOS À DIREITA */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <ContentPasteSearch color="inherit" fontSize="large" />
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            Consulta de Solicitações de Compra
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<Rule />}
            onClick={() => handleNavigate('erp/purchases/purchase/approval')}
            sx={{ fontWeight: 'bold', textTransform: 'none', height: 38 }}
          >
            Aprovação de Solicitações
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<AddShoppingCart />}
            onClick={() => handleNavigate('erp/purchases/purchase/request')}
            sx={{ fontWeight: 'bold', textTransform: 'none', height: 38 }}
          >
            Gerar Solicitação
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Consulte e acompanhe o histórico completo de solicitações de compra cadastradas no ERP Senior.
      </Typography>

      {/* PAINEL DE FILTROS */}
      <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box display="flex" alignItems="center" gap={1} mb={2.5}>
          <FilterList color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            Filtro
          </Typography>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-company-label">Empresa ERP</InputLabel>
              <Select
                labelId="select-company-label"
                value={companyId}
                label="Empresa ERP"
                onChange={handleCompanyChange}
                disabled={isLoadingCompanies}
              >
                {companyList.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    [{emp.id}] {emp.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-branch-label">Filial</InputLabel>
              <Select
                labelId="select-branch-label"
                value={branchId}
                label="Filial"
                onChange={e => {
                  setBranchId(Number(e.target.value));
                  handleResetSearchData();
                }}
                disabled={isLoadingCompanies || !currentCompany.filiais.length}
              >
                {currentCompany.filiais?.map(fil => (
                  <MenuItem key={fil.id} value={fil.id}>
                    [{fil.id}] {fil.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-status-label">Situação SC</InputLabel>
              <Select
                labelId="select-status-label"
                value={statusFilter}
                label="Situação SC"
                onChange={e => {
                  setStatusFilter(e.target.value);
                  handleResetSearchData();
                }}
              >
                <MenuItem value="ALL">Todas as Situações</MenuItem>
                <MenuItem value="ANA">Pendente de Aprovação</MenuItem>
                <MenuItem value="APR">Aprovada (Fila de Compras)</MenuItem>
                <MenuItem value="COT">Em Cotação</MenuItem>
                <MenuItem value="OCP">Ordem de Compra Gerada</MenuItem>
                <MenuItem value="FIN">Finalizada (Atendida / NF)</MenuItem>
                <MenuItem value="REP">Reprovada</MenuItem>
                <MenuItem value="CAN">Cancelada</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Nº da Solicitação"
              placeholder="Ex: 19575, 19580-19590"
              value={purchaseRequestNum}
              onChange={e => {
                setPurchaseRequestNum(e.target.value);
                handleResetSearchData();
              }}
              InputProps={{
                endAdornment: purchaseRequestNum ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setPurchaseRequestNum('');
                        handleResetSearchData();
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <GenericSearchInput
              label="Solicitante(s) ERP"
              source="erpUsers"
              value={requesterInput}
              onChange={val => {
                setRequesterInput(val);
                handleResetSearchData();
              }}
              multiple
              placeholder="Ex: 1, 2, 5-10"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <GenericSearchInput
              label="Produto ou Serviço"
              source="products"
              companyId={companyId}
              value={itemCodeInput}
              onChange={val => {
                setItemCodeInput(val);
                handleResetSearchData();
              }}
              multiple
              placeholder="Ex: 100.001, 100.002-100.005"
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Data Solicit. Inicial"
              type="date"
              value={startDate}
              onChange={e => {
                setStartDate(e.target.value);
                handleResetSearchData();
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: startDate ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setStartDate('');
                        handleResetSearchData();
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Data Solicit. Final"
              type="date"
              value={endDate}
              onChange={e => {
                setEndDate(e.target.value);
                handleResetSearchData();
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: endDate ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEndDate('');
                        handleResetSearchData();
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Filtro Rápido na Tela"
              placeholder="Filtre por solicitação, item, comprador, OC, cotação..."
              value={quickSearch}
              onChange={e => {
                setQuickSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {quickSearch && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setQuickSearch('');
                          setPage(0);
                        }}
                        sx={{ mr: 0.5 }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    )}
                    <Tooltip title="Filtra instantaneamente sobre os registros já listados na grade.">
                      <IconButton size="small" edge="end" color="primary">
                        <InfoOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => executeFilterSearch()}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <Search />}
              sx={{ height: '40px', fontWeight: 'bold', px: 4 }}
            >
              Pesquisar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* TABELA DE SOLICITAÇÕES */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <List color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold" color="primary">
              Resultados da Consulta ({filteredRequests.length})
            </Typography>
          </Box>
        </Box>

        <TableContainer sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    bgcolor: isDark ? 'grey.900' : 'grey.100',
                    color: 'text.primary',
                    fontWeight: 'bold',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                }}
              >
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'solicitacao'}
                    direction={sortField === 'solicitacao' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('solicitacao')}
                  >
                    Solicitação / Seq
                  </TableSortLabel>
                </TableCell>

                <TableCell>
                  <TableSortLabel
                    active={sortField === 'dataSolicitacao'}
                    direction={sortField === 'dataSolicitacao' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('dataSolicitacao')}
                  >
                    Data / Hora
                  </TableSortLabel>
                </TableCell>

                <TableCell>
                  <TableSortLabel
                    active={sortField === 'codUsuarioSolicitante'}
                    direction={sortField === 'codUsuarioSolicitante' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('codUsuarioSolicitante')}
                  >
                    Solicitante
                  </TableSortLabel>
                </TableCell>

                <TableCell>
                  <TableSortLabel
                    active={sortField === 'descricaoProduto'}
                    direction={sortField === 'descricaoProduto' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('descricaoProduto')}
                  >
                    Produto / Serviço
                  </TableSortLabel>
                </TableCell>

                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'quantidadeSolicitada'}
                    direction={sortField === 'quantidadeSolicitada' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('quantidadeSolicitada')}
                  >
                    Qtd. Solicitada
                  </TableSortLabel>
                </TableCell>

                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'overallStatus'}
                    direction={sortField === 'overallStatus' ? sortOrder : 'asc'}
                    onClick={() => handleSortClick('overallStatus')}
                  >
                    Situação
                  </TableSortLabel>
                </TableCell>

                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Buscando solicitações...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : !hasSearched && requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                    >
                      <InfoOutlined /> Clique no botão <b>&quot;Pesquisar&quot;</b> para consultar as solicitações de
                      compra.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nenhuma solicitação encontrada para os filtros aplicados.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRequests.map(row => (
                  <TableRow
                    key={`${row.empresa}-${row.solicitacao}-${row.sequencia}`}
                    hover
                    sx={{ '& td': { borderColor: theme.palette.divider } }}
                  >
                    <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ShoppingCart fontSize="small" color="action" />
                        <HighlightText text={String(row.solicitacao)} highlight={quickSearch} />
                        {' / '}
                        <HighlightText text={String(row.sequencia)} highlight={quickSearch} />
                      </Box>
                    </TableCell>

                    <TableCell sx={{ color: 'text.secondary' }}>
                      {row.dataSolicitacao} {formatMinutesToTime(row.horaSolicitacao)}
                    </TableCell>

                    <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                      <HighlightText text={String(row.codUsuarioSolicitante)} highlight={quickSearch} />
                      {' - '}
                      <HighlightText text={row.nomeUsuarioSolicitante || 'Não informado'} highlight={quickSearch} />
                    </TableCell>

                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Chip
                          label={row.produtoServico === 'P' ? 'Produto' : 'Serviço'}
                          size="small"
                          color={row.produtoServico === 'P' ? 'primary' : 'secondary'}
                          variant="outlined"
                          sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem' }}
                        />
                        <Typography variant="body2" fontWeight="bold" color="text.primary">
                          [<HighlightText text={row.produto || row.servico || ''} highlight={quickSearch} />]{' '}
                          <HighlightText
                            text={row.descricaoProduto || row.descricaoServico || ''}
                            highlight={quickSearch}
                          />
                        </Typography>
                      </Box>
                      {row.complementoProduto && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Comp: {row.complementoProduto}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatNumberPtBR(row.quantidadeSolicitada ?? row.quantidade)}
                    </TableCell>

                    <TableCell align="center">{getStatusChip(row)}</TableCell>

                    <TableCell align="center">
                      <Tooltip title="Ver Detalhes, Timeline e DANFE">
                        <IconButton size="small" onClick={() => setDetailItem(row)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredRequests.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Box>
      </Paper>

      <PurchaseRequestDetailsModal
        open={Boolean(detailItem)}
        onClose={() => setDetailItem(null)}
        item={detailItem}
        onObservationUpdated={newObs => {
          if (detailItem) {
            setDetailItem({ ...detailItem, observacaoSolicitacao: newObs });
          }
          executeFilterSearch();
        }}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={notification.type} variant="filled" sx={{ fontWeight: 'bold' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
